// Anti-Aging Follow-up Questionnaire (NAD+, MIC+B12, Glutathione, Vitamin B12, Metformin)
//
// Source of truth: Beluga Health "Anti-Aging Follow Up Questionnaire" — see
// docs/Anti-Aging Follow Up Questionnaire.docx (2026-05-15 drop from Beluga).
// Question order, conditional branching, consent body, and named-field mapping
// all come from that doc — do not reorder or reword without re-reading the
// source. PURPLE-marked questions (medicalConditions, selfReportedMeds,
// allergies) are extracted by backend mapAnswersToFormObj to top-level named
// fields and dropped from the Q/A array.
//
// Differences vs metabolicFollowUpData: no sex-at-birth, no pregnancy, no
// thyroid-history, no actively-taking gating, no current-medications
// multi-select. Height + weight rendered as the combined height-weight-bmi
// UI but split into two Q/A records at submit-time via splitHeightWeightAnswer
// (matches docx's two-separate-questions requirement).
//
// Entry point: only via the dashboard embed wrapper (?embed=1). The §0.A route
// guard at FollowUpForm.jsx redirects direct traffic, so this file is
// unreachable for self-serve patients.

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'This information helps your provider review your treatment.',
  // Anti-aging follow-ups don't gate on BMI.
  skipBmiCheck: true,
};

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Type 2 Diabetes, Hypertension, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily...',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis)...',
  required: true,
};

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question: 'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes...',
  required: true,
};

const medicationProgressQuestion = {
  id: 'medication-progress',
  type: 'single-select',
  question: 'How are you doing on the current medication?',
  options: [
    { label: 'Well', value: 'well' },
    { label: 'Not well', value: 'not-well' },
  ],
};

const medicationProgressDetailsQuestion = {
  id: 'medication-progress-details',
  type: 'textarea',
  question: 'Please tell us more about what you are experiencing',
  showIf: { field: 'medication-progress', value: 'not-well' },
  placeholder: 'Describe what you are experiencing...',
  required: true,
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'single-select',
  question: 'Have you had any side effects from the current medication?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'side-effects-details',
  type: 'textarea',
  question: 'Please tell us more about the side effects you are experiencing',
  showIf: { field: 'side-effects', value: 'yes' },
  placeholder: 'Describe your side effects...',
  required: true,
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here...',
  required: false,
};

// Body matches Beluga source-of-truth docx verbatim. Covers MIC+B12, Vitamin
// B12 (cobalamin), Glutathione, NAD+, and Metformin together — anti-aging
// patients can be on any combination, so the consent is product-agnostic.
const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'Consent (Informed Consent Statement)',
  consentText: `Indication for Use You are requesting treatment with one or more lifestyle-enhancement therapies including MIC+B12 injections, Vitamin B12 (cobalamin), Glutathione, NAD+ (Nicotinamide Adenine Dinucleotide), and/or Metformin to support metabolism, enhance energy production, promote cellular health, and potentially provide anti-aging benefits. These therapies are not intended for treatment of any specific medical condition.

Purpose of This Consent This form ensures you understand:
- The reason for prescribing these products
- Their potential benefits and risks
- The off-label status of certain components
- The monitoring and follow-up plan
- Your responsibilities during treatment

Background Information
- MIC+B12 combines lipotropic agents (Methionine, Inositol, Choline) with Vitamin B12 to aid fat metabolism and boost energy.
- Vitamin B12 (cobalamin) is essential for red blood cell production, neurological function, and DNA synthesis. Deficiency can arise from dietary insufficiency (e.g., vegan diets), malabsorption, or interactions with medications such as Metformin or proton-pump inhibitors.
- Glutathione is a natural antioxidant that reduces oxidative stress, supports detoxification, and enhances immune function.
- NAD+ is a coenzyme vital for mitochondrial energy production, DNA repair, and overall cellular health.
- Metformin is a prescription medication for type 2 diabetes that is being studied off-label for metabolic and anti-aging effects.

Off-Label Use Notice Glutathione, NAD+, and Metformin are not FDA-approved for wellness or anti-aging indications. Their use in this program is off-label, intended solely for health optimization and lifestyle enhancement.

Potential Benefits
- MIC+B12: Enhanced metabolism and fat burning
- Vitamin B12: Improved energy, neurological health, and cognitive function
- NAD+: Increased cellular energy and mitochondrial function
- Glutathione: Detoxification and antioxidant protection
- Metformin: Improved insulin sensitivity and metabolic efficiency
- All therapies: Increased vitality, reduced fatigue, and potential anti-aging effects

Potential Risks and Side Effects
- MIC+B12 — Common: Injection-site redness, swelling, pain; nausea; mild diarrhea. Serious: Allergic reaction (rash, itching, breathing difficulty); elevated liver enzymes; rare injection-site infection.
- Vitamin B12 — Common: Mild diarrhea; skin rash or itching; headache; nausea. Rare: Severe allergic reactions (anaphylaxis); hypokalemia with rapid correction; exacerbation of acne or rosacea.
- Glutathione — Common: GI discomfort; headache; skin reactions (rash, itching). Rare: Allergic reaction; localized skin lightening.
- NAD+ — Common: Nausea; fatigue; flushing; headache; abdominal cramping; dizziness. Less common: Anxiety; sweating; muscle cramps; injection-site pain or swelling.
- Metformin — Common: GI upset (nausea, bloating, diarrhea). Rare but serious: Lactic acidosis (especially with kidney or liver impairment); long-term vitamin B12 deficiency.

Risks and Considerations
- Allergic Reactions: Discontinue treatment and seek immediate care if you experience rash, swelling, or breathing difficulty.
- Kidney Function: Use Metformin cautiously with impaired kidney function.
- Supplement Interactions: Disclose all medications and supplements to avoid interactions, particularly those affecting absorption of Vitamin B12.

Monitoring and Follow-Up
- Attend all scheduled follow-up visits for assessment of therapy response and side-effect monitoring.

Patient Responsibilities By consenting to these therapies, you agree to:
- Follow the prescribed treatment plan exactly
- Report new or worsening symptoms promptly
- Avoid altering dosage or frequency without medical approval
- Attend all recommended follow-up appointments
- Inform your provider of any changes in medications, supplements, or health status

Patient Acknowledgment and Consent
- I have read and understood the information provided in this consent form.
- I understand the potential risks and benefits of using this product and the off-label nature of this treatment.
- I agree to adhere to the monitoring plan and to inform my healthcare provider of any side effects or concerns.

By selecting below, I voluntarily consent to the use of this product for the treatment of my symptoms.`,
  options: [
    { label: 'I have read the above information, I understand the risks and would like to proceed', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

export const antiAgingFollowUpQuestions = {
  heightWeight: heightWeightQuestion,
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  medicationProgress: medicationProgressQuestion,
  medicationProgressDetails: medicationProgressDetailsQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  additionalQuestions: additionalQuestionsQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

// Order matches the Beluga source-of-truth docx top-to-bottom. PURPLE
// (medical-conditions, self-reported-meds, allergies) sit between the
// height/weight pair and the health-changes branch — same position as the
// docx orders them.
export const antiAgingFollowUpFlow = [
  'height-weight',
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'health-changes',
  'health-changes-details',
  'medication-progress',
  'medication-progress-details',
  'side-effects',
  'side-effects-details',
  'additional-questions',
  'main-consent',
  'checkout',
];

export const getAntiAgingQuestionById = (id) =>
  Object.values(antiAgingFollowUpQuestions).find((q) => q.id === id);

// Builds the visible flow given current answers. Mirrors
// buildDynamicMetabolicFollowUpFlow but for the anti-aging question set.
// Three BLUE conditionals (health-changes-details, medication-progress-details,
// side-effects-details) gate on their preceding single-select.
export const buildDynamicAntiAgingFollowUpFlow = (answers /* , userData */) => {
  const flow = [];
  for (const questionId of antiAgingFollowUpFlow) {
    const question = getAntiAgingQuestionById(questionId);
    if (!question) continue;

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    flow.push(question);
  }
  return flow;
};

export default antiAgingFollowUpQuestions;
